/**
 * Harmonic Drift - Audio Engine (Enhanced Melodic Support)
 * Handles all Tone.js audio synthesis with advanced melodic features
 */

import * as Tone from 'tone';
import { CONFIG, NON_POLY_SYNTHS } from '../config/index.js';

class AudioEngine {
    constructor() {
        this.synths = {};
        this.effects = {};
        this.scale = CONFIG.scales.dorian;
        this.scaleName = 'dorian';
        this.initialized = false;
        this.activeInstruments = [];
        
        // Note name to semitone mapping
        this.noteToSemitone = {
            'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3,
            'E': 4, 'F': 5, 'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8,
            'Ab': 8, 'A': 9, 'A#': 10, 'Bb': 10, 'B': 11
        };
        
        // Callbacks
        this.onStep = null;
    }

    /**
     * Initialize the audio engine with a list of instruments
     * @param {Array} instrumentList - List of instrument definitions
     */
    async init(instrumentList) {
        if (!this.initialized) {
            await Tone.start();
            await this.setupEffects();
            this.setupScheduler();
            this.initialized = true;
        }

        // Clean up old synths
        this.disposeSynths();

        // Store active instruments
        this.activeInstruments = instrumentList;

        // Create synths for each instrument
        instrumentList.forEach(inst => {
            this.createSynth(inst);
        });
    }

    /**
     * Set up audio effects chain
     */
    async setupEffects() {
        this.effects.reverb = new Tone.Reverb({ 
            decay: CONFIG.audio.reverbDecay, 
            wet: CONFIG.audio.reverbWet 
        });
        await this.effects.reverb.generate();
        this.effects.reverb.toDestination();
        
        this.effects.delay = new Tone.FeedbackDelay("8n", CONFIG.audio.delayFeedback)
            .connect(this.effects.reverb);
        
        this.effects.filter = new Tone.Filter(20000, "lowpass")
            .connect(this.effects.delay);
        
        this.effects.limiter = new Tone.Limiter(-2)
            .connect(this.effects.filter);
    }

    /**
     * Set up the transport scheduler
     */
    setupScheduler() {
        Tone.Transport.scheduleRepeat((time) => {
            if (this.onStep) {
                this.onStep(time);
            }
        }, "16n");
    }

    /**
     * Create a synth for an instrument
     * @param {Object} inst - Instrument definition
     */
    createSynth(inst) {
        try {
            let synth;
            const opts = inst.options || {};
            const toneType = inst.toneType;

            // Handle different synth types appropriately
            if (NON_POLY_SYNTHS.includes(toneType)) {
                // These synths cannot be wrapped in PolySynth
                const SynthClass = Tone[toneType];
                synth = new SynthClass(opts);
            } else {
                // Standard synths can use PolySynth for polyphony
                const SynthClass = Tone[toneType] || Tone.Synth;
                synth = new Tone.PolySynth(SynthClass, opts);
                synth.maxPolyphony = 8; // Limit voices to prevent overload
            }

            // Route to effects chain (all synths go through limiter -> filter -> delay -> reverb)
            synth.connect(this.effects.limiter);

            // Set volume based on synth type
            synth.volume.value = CONFIG.audio.defaultVolume;
            if (toneType === 'NoiseSynth') synth.volume.value = CONFIG.audio.noiseVolume;
            if (toneType === 'MetalSynth') synth.volume.value = CONFIG.audio.metalVolume;

            this.synths[inst.id] = synth;
        } catch (e) {
            console.error(`Failed to create synth for ${inst.name}:`, e);
        }
    }

    /**
     * Play a note on a specific instrument
     * @param {string} instId - Instrument ID
     * @param {string} note - Note to play
     * @param {string} duration - Note duration
     * @param {number} time - Tone.js time
     * @param {number} velocity - Note velocity (0-1)
     */
    playNote(instId, note, duration, time, velocity = 0.8) {
        const synth = this.synths[instId];
        if (!synth) return;

        const inst = this.activeInstruments.find(i => i.id === instId);
        const toneType = inst ? inst.toneType : 'Synth';

        try {
            if (toneType === 'NoiseSynth') {
                synth.triggerAttackRelease(duration, time);
            } else {
                synth.triggerAttackRelease(note, duration, time, velocity);
            }
        } catch (e) {
            console.warn(`Error playing ${instId}:`, e.message);
        }
    }

    /**
     * Play a note with physics-based modulation
     * @param {string} instId - Instrument ID
     * @param {string} note - Note to play
     * @param {string} duration - Note duration
     * @param {number} time - Tone.js time
     * @param {Object} modulation - Physics modulation parameters
     */
    playNoteWithModulation(instId, note, duration, time, modulation = {}) {
        const synth = this.synths[instId];
        if (!synth) return;

        const inst = this.activeInstruments.find(i => i.id === instId);
        const toneType = inst ? inst.toneType : 'Synth';

        try {
            // Calculate modulated velocity
            const velocity = Math.min(1, Math.max(0.1, 0.7 * (modulation.volumeMod || 1)));
            
            // Apply detune if synth supports it (with safety checks)
            const detuneVal = modulation.detune;
            if (typeof detuneVal === 'number' && isFinite(detuneVal) && detuneVal !== 0) {
                try {
                    if (synth.detune !== undefined) {
                        synth.detune.value = detuneVal;
                    } else if (synth.voices) {
                        synth.set({ detune: detuneVal });
                    }
                } catch (e) { /* ignore detune errors */ }
            }

            // Apply FM modulation parameters if applicable
            if (toneType === 'FMSynth') {
                try {
                    if (typeof modulation.harmonicityMod === 'number' && isFinite(modulation.harmonicityMod)) {
                        synth.harmonicity.value = modulation.harmonicityMod;
                    }
                    if (typeof modulation.modIndexMod === 'number' && isFinite(modulation.modIndexMod)) {
                        synth.modulationIndex.value = modulation.modIndexMod;
                    }
                } catch (e) { /* ignore */ }
            }

            // Apply DuoSynth modulations
            if (toneType === 'DuoSynth') {
                try {
                    if (typeof modulation.vibratoMod === 'number' && isFinite(modulation.vibratoMod)) {
                        synth.vibratoAmount.value = modulation.vibratoMod;
                    }
                    if (typeof modulation.duoHarmonicityMod === 'number' && isFinite(modulation.duoHarmonicityMod)) {
                        synth.harmonicity.value = modulation.duoHarmonicityMod;
                    }
                } catch (e) { /* ignore */ }
            }

            // Apply PluckSynth modulations  
            if (toneType === 'PluckSynth') {
                try {
                    if (typeof modulation.dampeningMod === 'number' && isFinite(modulation.dampeningMod)) {
                        synth.dampening.value = modulation.dampeningMod;
                    }
                    if (typeof modulation.resonanceMod === 'number' && isFinite(modulation.resonanceMod)) {
                        synth.resonance.value = Math.min(0.99, modulation.resonanceMod);
                    }
                } catch (e) { /* ignore */ }
            }

            // Apply filter modulation for MonoSynth
            if (toneType === 'MonoSynth' && typeof modulation.filterMod === 'number') {
                try {
                    const filterFreq = 200 + modulation.filterMod * 3000;
                    synth.filterEnvelope.baseFrequency = filterFreq;
                } catch (e) { /* ignore */ }
            }

            // Play the note
            if (toneType === 'NoiseSynth') {
                synth.triggerAttackRelease(duration, time);
            } else {
                synth.triggerAttackRelease(note, duration, time, velocity);
            }

            // Apply reverb boost based on connections
            if (modulation.reverbBoost && this.effects.reverb) {
                const baseWet = CONFIG.audio.reverbWet;
                const boostedWet = Math.min(0.8, baseWet + modulation.reverbBoost);
                this.effects.reverb.wet.rampTo(boostedWet, 0.1);
                setTimeout(() => {
                    this.effects.reverb.wet.rampTo(baseWet, 0.5);
                }, 200);
            }

        } catch (e) {
            console.warn(`Error playing modulated ${instId}:`, e.message);
        }
    }

    /**
     * Get a note based on Y position
     * @param {number} y - Y coordinate
     * @param {number} height - Canvas height
     * @returns {string} Note name
     */
    getNoteFromY(y, height) {
        const pct = 1 - (y / height);
        const scaleLen = this.scale.length;
        const octaveShift = Math.floor(pct * 2);
        const index = Math.floor(pct * scaleLen) % scaleLen;
        let note = this.scale[index];
        
        if (octaveShift > 0 && note) {
            const noteName = note.slice(0, -1);
            const octave = parseInt(note.slice(-1)) + 1;
            note = noteName + octave;
        }
        
        return note || this.scale[0];
    }

    /**
     * Get a note from the scale by index
     * @param {number} index - Scale degree (can be negative or > scale length)
     * @param {number} baseOctave - Base octave
     * @returns {string} Note name with octave
     */
    getNoteByScaleDegree(index, baseOctave = 3) {
        const scaleLen = this.scale.length;
        const normalizedIndex = ((index % scaleLen) + scaleLen) % scaleLen;
        const octaveOffset = Math.floor(index / scaleLen);
        
        const note = this.scale[normalizedIndex];
        if (!note) return null;
        
        const noteName = note.slice(0, -1);
        const noteOctave = parseInt(note.slice(-1));
        const finalOctave = Math.max(1, Math.min(7, baseOctave + octaveOffset));
        
        return noteName + finalOctave;
    }

    /**
     * Get the interval in semitones between two notes
     * @param {string} note1 - First note
     * @param {string} note2 - Second note
     * @returns {number} Interval in semitones
     */
    getInterval(note1, note2) {
        const midi1 = this.noteToMidi(note1);
        const midi2 = this.noteToMidi(note2);
        return midi2 - midi1;
    }

    /**
     * Convert a note to MIDI number
     * @param {string} note - Note string (e.g., "C4")
     * @returns {number} MIDI note number
     */
    noteToMidi(note) {
        if (!note) return 60;
        const noteName = note.slice(0, -1);
        const octave = parseInt(note.slice(-1));
        const semitone = this.noteToSemitone[noteName] || 0;
        return (octave + 1) * 12 + semitone;
    }

    /**
     * Convert MIDI number to note
     * @param {number} midi - MIDI note number
     * @returns {string} Note string
     */
    midiToNote(midi) {
        const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        const octave = Math.floor(midi / 12) - 1;
        const noteIndex = midi % 12;
        return noteNames[noteIndex] + octave;
    }

    /**
     * Transpose a note by semitones
     * @param {string} note - Original note
     * @param {number} semitones - Semitones to transpose
     * @returns {string} Transposed note
     */
    transposeNote(note, semitones) {
        const midi = this.noteToMidi(note);
        return this.midiToNote(midi + semitones);
    }

    /**
     * Get a consonant interval note
     * @param {string} baseNote - Base note
     * @param {string} intervalType - Type of interval ('third', 'fifth', 'octave', etc.)
     * @returns {string} Resulting note
     */
    getConsonantNote(baseNote, intervalType) {
        const intervals = {
            'unison': 0,
            'minor2': 1,
            'major2': 2,
            'minor3': 3,
            'major3': 4,
            'perfect4': 5,
            'tritone': 6,
            'perfect5': 7,
            'minor6': 8,
            'major6': 9,
            'minor7': 10,
            'major7': 11,
            'octave': 12
        };
        
        const semitones = intervals[intervalType] || 0;
        return this.transposeNote(baseNote, semitones);
    }

    /**
     * Check if a note is in the current scale
     * @param {string} note - Note to check
     * @returns {boolean} Whether note is in scale
     */
    isInScale(note) {
        const noteName = note.slice(0, -1);
        return this.scale.some(scaleNote => scaleNote.startsWith(noteName));
    }

    /**
     * Snap a note to the nearest scale note
     * @param {string} note - Note to snap
     * @returns {string} Nearest scale note
     */
    snapToScale(note) {
        if (this.isInScale(note)) return note;
        
        const midi = this.noteToMidi(note);
        const octave = Math.floor(midi / 12) - 1;
        
        // Find nearest scale note
        let nearestNote = this.scale[0];
        let minDistance = Infinity;
        
        for (const scaleNote of this.scale) {
            const scaleMidi = this.noteToMidi(scaleNote);
            // Check same octave and adjacent octaves
            for (let oct = -1; oct <= 1; oct++) {
                const testMidi = scaleMidi + oct * 12;
                const distance = Math.abs(testMidi - midi);
                if (distance < minDistance) {
                    minDistance = distance;
                    const noteName = scaleNote.slice(0, -1);
                    nearestNote = noteName + (octave + oct);
                }
            }
        }
        
        return nearestNote;
    }

    /**
     * Set the musical scale
     * @param {string} scaleName - Scale name from CONFIG.scales
     */
    setScale(scaleName) {
        if (CONFIG.scales[scaleName]) {
            this.scale = CONFIG.scales[scaleName];
            this.scaleName = scaleName;
        }
    }

    /**
     * Get the current scale name
     * @returns {string} Current scale name
     */
    getScaleName() {
        return this.scaleName;
    }

    /**
     * Set the filter frequency (for entropy effect)
     * @param {number} frequency - Filter frequency in Hz
     */
    setFilterFrequency(frequency) {
        if (this.effects.filter) {
            this.effects.filter.frequency.rampTo(frequency, 0.1);
        }
    }

    /**
     * Set the tempo
     * @param {number} bpm - Beats per minute
     */
    setTempo(bpm) {
        Tone.Transport.bpm.value = bpm;
    }

    /**
     * Start the transport
     */
    start() {
        Tone.Transport.start();
    }

    /**
     * Pause the transport
     */
    pause() {
        Tone.Transport.pause();
    }

    /**
     * Stop the transport
     */
    stop() {
        Tone.Transport.stop();
    }

    /**
     * Dispose of all synths
     */
    disposeSynths() {
        Object.values(this.synths).forEach(synth => {
            try {
                synth.dispose();
            } catch (e) {
                // Ignore disposal errors
            }
        });
        this.synths = {};
    }

    /**
     * Test if synth options are valid
     * @param {string} toneType - Synth type name
     * @param {Object} options - Synth options
     * @returns {boolean} Whether the configuration is valid
     */
    static testSynthConfig(toneType, options) {
        try {
            let test;
            if (NON_POLY_SYNTHS.includes(toneType)) {
                const TestSynth = Tone[toneType];
                test = new TestSynth(options);
            } else {
                const TestSynth = Tone[toneType] || Tone.Synth;
                test = new Tone.PolySynth(TestSynth, options);
            }
            test.dispose();
            return true;
        } catch (e) {
            console.error('Invalid synth config:', e);
            return false;
        }
    }

    /**
     * Get current Tone.js time
     * @returns {number} Current time
     */
    now() {
        return Tone.now();
    }
}

// Export singleton instance
export const audioEngine = new AudioEngine();